/* @jsx h */
import { defineComponent, h, onMounted, ref } from "vue";
import { useRoute } from "vue-router";
import { QRCodeComponent, useLocalization } from "@lemmonhq/uikit";
import { useTrpc } from "@app/vue/providers/TrpcProvider";
import type { PublicGiftCard } from "@app/server/trpc/routers/gift-cards";
import appleWalletBadge from "../../../../../../packages/business/assets/images/partners/apple-wallet.svg";
import googleWalletBadge from "../../../../../../packages/business/assets/images/partners/google-wallet.svg";
//
const STATUS_CONFIG: Record<
  string,
  { badge: string; labelKey: string; icon: string }
> = {
  active: {
    badge: "badge-success",
    labelKey: "customer.gift_card.status_active",
    icon: "check_circle",
  },
  redeemed: {
    badge: "badge-neutral",
    labelKey: "customer.gift_card.status_redeemed",
    icon: "paid",
  },
  expired: {
    badge: "badge-warning",
    labelKey: "customer.gift_card.status_expired",
    icon: "schedule",
  },
  void: {
    badge: "badge-error",
    labelKey: "customer.gift_card.status_void",
    icon: "cancel",
  },
};

export const GiftCardPage = defineComponent({
  name: "GiftCardPage",
  setup() {
    const route = useRoute();
    const trpc = useTrpc();
    const { t } = useLocalization();
    const card = ref<PublicGiftCard | null>(null);
    const error = ref<string | null>(null);
    const loading = ref(true);

    onMounted(async () => {
      const code = route.params.code as string;
      try {
        card.value = await trpc.giftCards.getByCode.query({ code });
      } catch (e: any) {
        error.value = e?.message?.includes("NOT_FOUND")
          ? t("customer.gift_card.not_found")
          : t("customer.gift_card.failed_to_load");
      } finally {
        loading.value = false;
      }
    });

    return () => {
      if (loading.value) {
        return (
          <div class="flex items-center justify-center min-h-screen bg-base-100">
            <span class="loading loading-spinner loading-lg text-primary" />
          </div>
        );
      }

      if (error.value || !card.value) {
        return (
          <div class="flex flex-col items-center justify-center min-h-screen gap-4 p-8 bg-base-100">
            <span class="material-symbols-outlined text-7xl text-error/40">
              card_giftcard
            </span>
            <p class="text-base-content/50 text-center">
              {error.value ?? t("customer.gift_card.not_found")}
            </p>
          </div>
        );
      }

      const balance = Number(card.value.current_balance);
      const initial = Number(card.value.initial_value);
      const usedPct =
        initial > 0 ? Math.round(((initial - balance) / initial) * 100) : 0;
      const status = STATUS_CONFIG[card.value.status] ?? {
        badge: "badge-neutral",
        labelKey: "",
        icon: "info",
      };
      const isActive = card.value.status === "active";
      const pageUrl = window.location.href;

      return (
        <div class="min-h-screen bg-base-200 flex flex-col items-center py-10 px-4">
          <div class="w-full max-w-sm flex flex-col gap-5">
            {/* ── Gift card hero ── */}
            <div
              class={`rounded-3xl overflow-hidden shadow-xl ${isActive ? "bg-primary text-primary-content" : "bg-base-300 text-base-content/50"}`}
            >
              {/* top strip */}
              <div class="flex items-center justify-between px-6 pt-6 pb-4">
                <div class="flex items-center gap-2 opacity-80">
                  <span class="material-symbols-outlined text-2xl">
                    card_giftcard
                  </span>
                  <span class="text-xs font-semibold uppercase tracking-widest">
                    {t("customer.gift_card.gift_card")}
                  </span>
                </div>
                <span class={`badge badge-sm ${status.badge} gap-1`}>
                  <span
                    class="material-symbols-outlined"
                    style="font-size:12px"
                  >
                    {status.icon}
                  </span>
                  {status.labelKey ? t(status.labelKey) : card.value.status}
                </span>
              </div>

              {/* balance */}
              <div class="px-6 pb-2">
                <p class="text-xs opacity-60 uppercase tracking-widest mb-1">
                  {t("customer.gift_card.current_balance")}
                </p>
                <p class="text-5xl font-bold tracking-tight">
                  {card.value.currency} {balance.toFixed(2)}
                </p>
              </div>

              {/* progress bar */}
              {initial > 0 && (
                <div class="px-6 pt-3 pb-5">
                  <div class="flex justify-between text-xs opacity-60 mb-1">
                    <span>
                      {t("customer.gift_card.used_percent", [usedPct])}
                    </span>
                    <span>
                      {t("customer.gift_card.of_amount", [
                        card.value.currency,
                        initial.toFixed(2),
                      ])}
                    </span>
                  </div>
                  <div class="w-full bg-primary-content/20 rounded-full h-1.5">
                    <div
                      class="bg-primary-content rounded-full h-1.5 transition-all"
                      style={`width:${usedPct}%`}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* ── QR code ── */}
            <div class="bg-base-100 rounded-3xl shadow-md p-6 flex flex-col items-center gap-3">
              <div class="w-full max-w-[220px]">
                <QRCodeComponent url={pageUrl} />
              </div>
              <p class="text-xs text-base-content/40 text-center leading-snug">
                {t("customer.gift_card.show_code_to_redeem")}
              </p>
            </div>

            {/* ── Restaurant info ── */}
            <div class="bg-base-100 rounded-3xl shadow-md px-6 py-5 flex flex-col gap-3">
              <p class="text-xs font-semibold text-base-content/40 uppercase tracking-widest">
                {t("customer.gift_card.issued_by")}
              </p>
              <div class="flex items-start gap-3">
                <span class="material-symbols-outlined text-primary mt-0.5">
                  storefront
                </span>
                <div>
                  <p class="font-semibold text-base-content">
                    {card.value.restaurant_name}
                  </p>
                  <p class="text-sm text-base-content/60 mt-0.5">
                    {card.value.restaurant_address}
                  </p>
                  {card.value.receipt_phone && (
                    <a
                      href={`tel:${card.value.receipt_phone}`}
                      class="text-sm text-primary mt-0.5 flex items-center gap-1"
                    >
                      <span
                        class="material-symbols-outlined"
                        style="font-size:14px"
                      >
                        call
                      </span>
                      {card.value.receipt_phone}
                    </a>
                  )}
                </div>
              </div>
            </div>

            {/* ── Wallet badges ── */}
            <div class="flex items-center justify-center gap-3 opacity-80">
              <img
                src={appleWalletBadge}
                alt={t("customer.gift_card.add_to_apple_wallet")}
                class="h-10 pointer-events-none"
              />
              <img
                src={googleWalletBadge}
                alt={t("customer.gift_card.add_to_google_wallet")}
                class="h-10 pointer-events-none"
              />
            </div>

            {/* footer */}
            <p class="text-xs text-base-content/25 text-center pb-4">
              {t("customer.gift_card.powered_by", ["Lemmon"])}
            </p>
          </div>
        </div>
      );
    };
  },
});
